import { Suspense, useEffect } from 'react'
import { Outlet, useParams } from 'react-router-dom'
import { useClientStore } from '../../stores/clientStore'
import { useUIStore } from '../../stores/uiStore'
import { cn } from '../../lib/cn'
import { LoadingState } from '../ui/LoadingState'
import { ShortcutModals } from '../shortcuts/ShortcutModals'
import { Sidebar } from './Sidebar'
import { Topbar } from './Topbar'

const FRAME_WIDTH = {
  desktop: '100%',
  tablet:  '1024px',
  mobile:  '414px',
} as const

export function AppShell() {
  const { clientId } = useParams<{ clientId: string }>()
  const setCurrentClientId = useClientStore((s) => s.setCurrentClientId)
  const viewMode = useUIStore((s) => s.viewMode)
  const mobileOpen = useUIStore((s) => s.mobileSidebarOpen)
  const setMobileOpen = useUIStore((s) => s.setMobileSidebarOpen)

  useEffect(() => {
    if (clientId) setCurrentClientId(clientId)
  }, [clientId, setCurrentClientId])

  useEffect(() => {
    document.documentElement.dataset.view = viewMode
    return () => {
      delete document.documentElement.dataset.view
    }
  }, [viewMode])

  const isFramed = viewMode !== 'desktop'

  const shell = (
    <div className="@container/shell relative h-full flex bg-white text-black">
      <Sidebar />
      {mobileOpen && (
        <button
          aria-label="Close menu"
          onClick={() => setMobileOpen(false)}
          className="fixed inset-0 z-30 bg-black/30 @md/shell:hidden"
        />
      )}
      <div className="flex-1 flex flex-col min-w-0 bg-black/[0.015]">
        <Topbar />
        <main className="flex-1 overflow-y-auto @container">
          <div className="w-full px-4 @md:px-6 @4xl:px-8 @5xl:px-10 py-6 @4xl:py-8">
            <Suspense fallback={<LoadingState page />}>
              <Outlet />
            </Suspense>
          </div>
        </main>
      </div>
      <ShortcutModals />
    </div>
  )

  if (!isFramed) {
    return <div className="h-screen overflow-hidden">{shell}</div>
  }

  return (
    <div className="h-screen overflow-y-auto bg-black/[0.05] flex items-center justify-center py-4">
      <div
        className={cn(
          'h-full bg-white rounded-2xl overflow-hidden shadow-soft-lg ring-8 ring-black/5',
          'flex flex-col',
        )}
        style={{ width: FRAME_WIDTH[viewMode], maxWidth: '100%' }}
      >
        {shell}
      </div>
    </div>
  )
}
